import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Popper, { type PopperPlacementType } from '@mui/material/Popper';
import type { SxProps, Theme } from '@mui/material/styles';
import { useLayoutEffect, useState } from 'react';

import { mergeSx } from '@/utils/mergeSx';

import { formattedValueHintStyles as sx } from './FormattedValueHint.styles';

const GAP = 8;
const MIN_WIDTH = 150;

const MODIFIERS = [{ name: 'offset', options: { offset: [0, 10] } }];

type Props = {
	message: string;
	isError?: boolean;
	open: boolean;
	anchorEl: HTMLElement | null;
	placement?: PopperPlacementType;
	customSx?: SxProps<Theme>;
	boundaryEl?: HTMLElement | null;
	subtractWidth?: number;
};

export const FormattedValueHint = ({
	message,
	isError = false,
	open,
	anchorEl,
	placement = 'bottom-start',
	customSx,
	boundaryEl,
	subtractWidth = 0
}: Props) => {
	const [bubbleEl, setBubbleEl] = useState<HTMLElement | null>(null);
	const [boundaryWidth, setBoundaryWidth] = useState(0);
	const [shiftPx, setShiftPx] = useState(0);

	useLayoutEffect(() => {
		if (!boundaryEl) return;

		const observer = new ResizeObserver(([entry]) => {
			setBoundaryWidth(entry.contentRect.width);
		});
		observer.observe(boundaryEl);

		return () => observer.disconnect();
	}, [boundaryEl]);

	const maxWidthPx = boundaryWidth
		? Math.max(boundaryWidth - subtractWidth - GAP, MIN_WIDTH)
		: undefined;

	useLayoutEffect(() => {
		if (!open || !anchorEl || !boundaryEl || !bubbleEl) return;

		const anchorRect = anchorEl.getBoundingClientRect();
		const boundaryRect = boundaryEl.getBoundingClientRect();
		const bubbleWidth = bubbleEl.getBoundingClientRect().width;

		const availableToRight =
			boundaryRect.right - anchorRect.left - subtractWidth - GAP;
		const overflow = Math.max(0, bubbleWidth - availableToRight);
		const maxShift = Math.max(0, anchorRect.left - boundaryRect.left);

		// eslint-disable-next-line react-hooks/set-state-in-effect -- derives the shift from a DOM measurement that can only be read after layout
		setShiftPx(Math.min(overflow, maxShift));
	}, [
		open,
		anchorEl,
		boundaryEl,
		bubbleEl,
		subtractWidth,
		maxWidthPx,
		message
	]);

	return (
		<Popper
			open={open && !!anchorEl}
			anchorEl={anchorEl}
			placement={placement}
			modifiers={MODIFIERS}
			sx={sx.popper}
		>
			<Box sx={sx.wrapper}>
				<Box
					ref={setBubbleEl}
					component='span'
					sx={mergeSx(
						sx.bubble,
						isError && sx.bubbleError,
						sx.bubbleDynamic(maxWidthPx, shiftPx),
						customSx
					)}
				>
					{isError ? <ErrorOutlineIcon /> : <InfoOutlinedIcon />}
					<Box component='span'>{message}</Box>
				</Box>
				<Box sx={sx.arrow} />
			</Box>
		</Popper>
	);
};
